import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  SxProps,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import { v2MenuItemDangerClass } from '@onevision-resources/design-theme';
import { MouseEvent, useMemo, useState } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';

export interface EllipsesMenuOption {
  id?: string;
  label: string;
  icon?: string;
  iconLibrary?: IconLibrary;
  iconColor?: string;
  testId?: string;
  onClick?: (event?: MouseEvent<HTMLElement>) => void;
  subItems?: EllipsesMenuOption[];
  selected?: boolean;
  danger?: boolean;
  disabled?: boolean;
}

interface EllipsesMenuProps {
  options: EllipsesMenuOption[];
  icon?: string;
  iconLibrary?: IconLibrary;
  triggerTestId?: string;
  title?: string;
  isLoading?: boolean;
  disabled?: boolean;
  showTitle?: boolean;
  sx?: SxProps<Theme>;
  open?: boolean;
  anchorEl?: null | HTMLElement;
  onClose?: () => void;
  hideTrigger?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const EllipsesMenu = ({
  options,
  icon = 'more_vert',
  iconLibrary,
  triggerTestId,
  title = 'More',
  isLoading = false,
  disabled = false,
  showTitle = false,
  sx,
  open: openProp,
  anchorEl: anchorElProp,
  onClose,
  hideTrigger = false,
  onOpenChange,
}: EllipsesMenuProps) => {
  const [internalAnchorEl, setInternalAnchorEl] = useState<null | HTMLElement>(null);
  const [nestedAnchorEl, setNestedAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedNestingOption, setSelectedNestingOption] = useState<
    EllipsesMenuOption['id'] | null
  >(null);

  const [filter, setFilter] = useState('');

  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : Boolean(internalAnchorEl);
  const anchorEl = isControlled ? (anchorElProp ?? null) : internalAnchorEl;
  const nestedOpen = Boolean(nestedAnchorEl);

  const handleMainMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setInternalAnchorEl(event.currentTarget);
    onOpenChange?.(true);
  };

  const handleMainMenuClose = (event?: MouseEvent<HTMLElement>) => {
    event?.stopPropagation();
    setInternalAnchorEl(null);
    setNestedAnchorEl(null);
    setSelectedNestingOption(null);
    setFilter('');
    onClose?.();
    onOpenChange?.(false);
  };

  const handleItemClick = (event: MouseEvent<HTMLElement>, option: EllipsesMenuOption) => {
    event.stopPropagation();
    if (option.subItems && option.subItems.length > 0) {
      setNestedAnchorEl(event.currentTarget);
      setSelectedNestingOption(option.id || option.label);
      setFilter('');
    } else {
      option.onClick?.(event);
      handleMainMenuClose(event);
    }
  };

  // there are a bunch of limitations while using native autocomplete inside a double nested
  // menu dialogs, like handling stopPropagation and etc, therefore it was decided to proceed
  // with custom filtering logic instead
  const filteredNestedSubItems = useMemo(() => {
    const option = options.find(
      (i) => (i.id && i.id === selectedNestingOption) || i.label === selectedNestingOption,
    );

    return option?.subItems?.filter((item) =>
      item.label.toLowerCase().includes(filter.toLowerCase()),
    );
  }, [options, selectedNestingOption, filter]);

  // Inert outside `V2ThemeProvider` — v1 defines no styling for it.
  const getMenuItemClassName = (danger?: boolean) =>
    danger ? v2MenuItemDangerClass : undefined;

  // casting the component as a React.ElementType to avoid union type issues
  const ButtonComponent: React.ElementType = showTitle ? Button : IconButton;
  const triggerBaseSx: SxProps<Theme> = {
    padding: 0,
    textTransform: 'none',
    ...(showTitle ? {} : { width: '1.75rem', height: '1.75rem' }),
  };
  const triggerSx: SxProps<Theme> = [
    triggerBaseSx,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- MUI sx-merge pattern
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  return (
    <>
      {!hideTrigger && (
        <Tooltip title={title}>
          <ButtonComponent
            aria-controls={open ? 'ellipses-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            data-testid={triggerTestId}
            size="small"
            sx={triggerSx}
            onClick={handleMainMenuOpen}
            disabled={isLoading || disabled}
          >
            {isLoading ? (
              <CircularProgress size="1.1rem" />
            ) : (
              <DynamicIcon name={icon} library={iconLibrary} />
            )}
            {!!showTitle && (
              <Typography variant="body2" sx={{ marginLeft: '0.5rem' }}>
                {title}
              </Typography>
            )}
          </ButtonComponent>
        </Tooltip>
      )}

      {/* Main menu */}
      <Menu anchorEl={anchorEl} open={open} onClose={() => handleMainMenuClose()}>
        {options.map((option) => (
          <MenuItem
            key={option.label}
            className={getMenuItemClassName(option.danger)}
            data-testid={option.testId}
            onClick={(event) => handleItemClick(event, option)}
            selected={option.selected}
            disabled={option.disabled}
          >
            {option.icon ? (
              <ListItemIcon>
                <DynamicIcon name={option.icon} library={option.iconLibrary} />
              </ListItemIcon>
            ) : null}
            <ListItemText>
              <Typography noWrap variant="body2">
                {option.label}
              </Typography>
            </ListItemText>
            {!!option.subItems?.length && (
              <DynamicIcon
                name="keyboard_arrow_right"
                sx={{ marginLeft: 'auto', transform: nestedOpen ? 'rotate(90deg)' : '' }}
              />
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Nested menu if applicable */}
      {selectedNestingOption ? (
        <Menu
          anchorEl={nestedAnchorEl}
          open={nestedOpen}
          onClose={() => handleMainMenuClose()}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          sx={{ maxHeight: '40rem' }}
        >
          <Box sx={{ p: 1, pt: 0 }}>
            <TextField
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter options..."
              variant="standard"
              fullWidth
              autoFocus
            />
          </Box>
          {filteredNestedSubItems && filteredNestedSubItems.length > 0 ? (
            filteredNestedSubItems.map((subItem) => (
              <MenuItem
                key={subItem.id || subItem.label}
                className={getMenuItemClassName(subItem.danger)}
                data-testid={subItem.testId}
                onClick={(event) => {
                  event.stopPropagation();
                  subItem.onClick?.(event);
                }}
              >
                {subItem.icon ? (
                  <ListItemIcon>
                    <DynamicIcon
                      name={subItem.icon}
                      color={subItem.iconColor}
                      filled={!!subItem.iconColor}
                    />
                  </ListItemIcon>
                ) : null}
                <ListItemText sx={{ width: '10rem' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {subItem.label}
                  </Typography>
                </ListItemText>
              </MenuItem>
            ))
          ) : (
            <MenuItem className={getMenuItemClassName()} disabled>
              <ListItemText>
                <Typography variant="body2">No matches</Typography>
              </ListItemText>
            </MenuItem>
          )}
        </Menu>
      ) : null}
    </>
  );
};
